import React, { useState, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  threshold?: number;
  refreshDistance?: number;
  disabled?: boolean;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  children,
  threshold = 60,
  refreshDistance = 80,
  disabled = false,
}) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [canRefresh, setCanRefresh] = useState(false);

  const startY = useRef(0);
  const currentY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || isRefreshing) return;

      // Only start pull-to-refresh if we're at the top of the container
      const container = containerRef.current;
      if (!container || container.scrollTop > 0) return;

      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    },
    [disabled, isRefreshing]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isPulling || disabled || isRefreshing) return;

      currentY.current = e.touches[0].clientY;
      const distance = Math.max(0, currentY.current - startY.current);

      // Apply resistance to the pull distance for a more natural feel
      const resistance = 0.5;
      const adjustedDistance = distance * resistance;

      setPullDistance(adjustedDistance);
      setCanRefresh(adjustedDistance >= threshold);

      // Prevent scrolling when pulling down
      if (distance > 0) {
        e.preventDefault();
      }
    },
    [isPulling, disabled, isRefreshing, threshold]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling || disabled) return;

    setIsPulling(false);

    if (canRefresh && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
      }
    }

    setPullDistance(0);
    setCanRefresh(false);
  }, [isPulling, disabled, canRefresh, isRefreshing, onRefresh]);

  // Calculate the refresh indicator opacity and rotation
  const indicatorOpacity = Math.min(1, pullDistance / threshold);
  const indicatorRotation = (pullDistance / threshold) * 180;

  // Determine the refresh indicator state
  const getRefreshIndicatorContent = () => {
    if (isRefreshing) {
      return (
        <div className="flex items-center gap-2">
          <RefreshCw className="animate-spin" size={20} style={{ color: '#31C48D' }} />
          <span style={{ color: '#31C48D' }}>Refreshing...</span>
        </div>
      );
    }

    if (canRefresh) {
      return (
        <div className="flex items-center gap-2">
          <RefreshCw size={20} style={{ color: '#FFD03C', transform: 'rotate(180deg)' }} />
          <span style={{ color: '#FFD03C' }}>Release to refresh</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <RefreshCw
          size={20}
          style={{
            color: '#9CA3AF',
            transform: `rotate(${indicatorRotation}deg)`,
            transition: isPulling ? 'none' : 'transform 0.3s ease',
          }}
        />
        <span style={{ color: '#9CA3AF' }}>Pull to refresh</span>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative overflow-auto h-full"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `translateY(${isPulling || isRefreshing ? Math.min(pullDistance, refreshDistance) : 0}px)`,
        transition: isPulling ? 'none' : 'transform 0.3s ease',
      }}
    >
      {/* Refresh Indicator */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-center py-4 text-sm font-medium"
        style={{
          transform: `translateY(${-60 + (isPulling || isRefreshing ? Math.min(pullDistance, refreshDistance) : 0)}px)`,
          opacity: isPulling || isRefreshing ? indicatorOpacity : 0,
          transition: isPulling ? 'none' : 'all 0.3s ease',
          background: 'rgba(26, 30, 45, 0.95)',
          backdropFilter: 'blur(8px)',
          borderRadius: '0 0 16px 16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        }}
      >
        {getRefreshIndicatorContent()}
      </div>

      {/* Content */}
      <div
        style={{
          paddingTop:
            isPulling || isRefreshing ? `${Math.min(pullDistance, refreshDistance)}px` : '0px',
          transition: isPulling ? 'none' : 'padding-top 0.3s ease',
        }}
      >
        {children}
      </div>
    </div>
  );
};
